import { Alert, Button, Checkbox, FormControlLabel, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";

import { api, ensureCsrf, errorMessage } from "../api/client";
import { Section, Seo } from "../marketing/MarketingLayout";
import { track } from "../marketing/analytics";

export function ContactPage({ kind: kindProp }: { kind?: "demo" | "contact" }) {
  const [params] = useSearchParams();
  const kind = (params.get("kind") as "demo" | "contact" | "enterprise" | null) ?? kindProp ?? "contact";
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", employees: "", industry: "", message: "", consent_marketing: false, website: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const utm = Object.fromEntries([...params.entries()].filter(([k]) => k.startsWith("utm_")));
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    await ensureCsrf();
    const { error: err } = await api.POST("/api/v1/website/leads/capture/", { body: { kind, ...form, source: window.location.pathname, utm } });
    if (err) { setError(errorMessage(err)); return; }
    track("lead_submitted", { kind });
    setDone(true);
  }
  const title = kind === "demo" ? "Book a demo" : kind === "enterprise" ? "Talk to sales" : "Contact us";
  return (
    <Section>
      <Seo title={title} />
      <Grid container spacing={6}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="h1" sx={{ fontSize: "2.4rem", mb: 1 }}>{title}</Typography>
          <Typography color="text.secondary">{kind === "demo" ? "A 30-minute walkthrough with a manufacturing specialist, using your parts if you like." : "We reply within one business day."}</Typography>
          <Typography variant="body2" sx={{ mt: 3 }}>Prefer email? <a href="mailto:sales@cloudforge.local">sales@cloudforge.local</a></Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          {done ? <Alert severity="success">Thanks! Check your inbox for a confirmation; we'll be in touch shortly.</Alert> : (
            <form onSubmit={submit}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Name" value={form.name} onChange={set("name")} required />
                  <TextField label="Work email" type="email" value={form.email} onChange={set("email")} required />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Company" value={form.company} onChange={set("company")} required />
                  <TextField label="Phone" value={form.phone} onChange={set("phone")} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField select label="Employees" value={form.employees} onChange={set("employees")}>{["1-10", "11-50", "51-200", "201-1000", "1000+"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>
                  <TextField select label="Industry" value={form.industry} onChange={set("industry")}>{["Machine shop", "Fabrication", "Contract manufacturing", "Equipment OEM", "Other"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>
                </Stack>
                <TextField label="What would you like to see?" multiline minRows={3} value={form.message} onChange={set("message")} />
                <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: -9999 }} aria-hidden />
                <FormControlLabel control={<Checkbox checked={form.consent_marketing} onChange={set("consent_marketing")} />} label="Send me product updates (optional)" />
                <Button type="submit" variant="contained" size="large">{kind === "demo" ? "Request demo" : "Send"}</Button>
              </Stack>
            </form>
          )}
        </Grid>
      </Grid>
    </Section>
  );
}
